/**
 * Reads the operator-facing message out of a failed `/auth/token/` response.
 *
 * The endpoint is SimpleJWT's `TokenObtainPairView`, and it answers a failed
 * sign-in in two different shapes, so reading only one of them leaves the other
 * silent:
 *
 *   401 {"detail": "No active account found with the given credentials"}
 *   400 {"username": ["This field may not be blank."], "password": [...]}
 *
 * The second shape is reachable from the login form: it submits `noValidate`
 * with no client-side rules, so an empty field reaches the serializer.
 *
 * Anything else — a network failure, a 500, an HTML error page from a proxy —
 * carries no message worth showing, and falls back to the caller's string.
 * Keeping the reads structural (a `detail` string, or one of the field names
 * below mapped to strings) is what keeps a proxy's HTML page out of the banner:
 * it arrives as a bare string rather than either shape, and is never returned.
 *
 * The returned string is rendered as a React child, which escapes it. It is NOT
 * sanitized here, so a custom snackbar renderer must never pass it through
 * `dangerouslySetInnerHTML` — that would turn this into a backend-fed XSS sink,
 * on the same origin as the tokens in localStorage.
 */

// A banner is one or two lines. A message past this is a backend fault leaking
// a stack trace or a dump, which the fallback states more usefully.
const MAX_LENGTH = 300;

// DRF collects errors that belong to no single field under this key, so its
// name reads as noise in front of the message rather than as a useful label.
const NON_FIELD_KEY = "non_field_errors";

// The only keys this reads, rather than every key the body happens to carry.
// The backend deploys as a Lambda container image, so a failure upstream of
// Django answers in its own shape — API Gateway's {"message": ...}, or a
// function error's {"errorType": ..., "stackTrace": [...]}, whose stackTrace is
// an array of strings and so would otherwise read as a field's errors and put
// server paths in front of a visitor who is not even signed in. Those bodies
// name none of these keys, so they reach the fallback on their shape rather
// than on being longer than MAX_LENGTH.
const READABLE_FIELDS = [NON_FIELD_KEY, "username", "password"] as const;

/** The keys the banner can report, for the login form to assert itself against. */
export type ReadableLoginField = (typeof READABLE_FIELDS)[number];

const isFieldMap = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** One field's errors, as DRF may send them: a list of strings, or a string. */
const fieldMessages = (value: unknown): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string");
  }
  return [];
};

/** `non_field_errors` -> `Non field errors`, `username` -> `Username`. */
const humanizeFieldName = (field: string): string => {
  const spaced = field.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const withinLimits = (message: string, fallback: string): string => {
  const trimmed = message.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_LENGTH ? trimmed : fallback;
};

export const LOGIN_ERROR_FALLBACK = "Unable to log in. Please try again.";

export const extractLoginErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const data = (error as { data?: unknown } | null)?.data;
  if (!isFieldMap(data)) return fallback;

  if (typeof data.detail === "string") {
    return withinLimits(data.detail, fallback);
  }

  const messages = READABLE_FIELDS.flatMap((field) =>
    fieldMessages(data[field]).map((message) =>
      field === NON_FIELD_KEY
        ? message
        : `${humanizeFieldName(field)}: ${message}`,
    ),
  );

  return messages.length ? withinLimits(messages.join(" "), fallback) : fallback;
};
